// GroupDetailsDrawer — opened by clicking a group card. An editable form (name,
// allow-join-by-link card with the group's real join link + QR, and a members
// section that toggles between a read-only list and the `UsersPicker`)
// with dirty-tracking (Discard / Save changes / "Saved" badge) and a danger
// Delete confirm. Driven by the selected group object from the sections list
// (no extra fetch); PATCH/DELETE /groups/:id.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import {
  Avatar,
  Button,
  Chip,
  ConfirmModal,
  Drawer,
  Field,
  Icon,
  IconButton,
  Input,
  Switch,
  UsersPicker,
} from '@/components/common';
import {
  useDeleteGroupMutation,
  useUpdateGroupMutation,
  type GroupItem,
} from '@/api/endpoints/groupsApi';
import { ADMIN_PANEL_URL } from '@/config/config';
import { copyText } from '@/utils/clipboard';
import useModuleAccess from '@/hooks/useModuleAccess';
import { useGetUsersSelectionQuery } from '@/api/endpoints/usersApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import { toastApiError } from '@/utils/apiErrorToast';

interface GroupDetailsDrawerProps {
  open: boolean;
  group: GroupItem | undefined;
  onClose: () => void;
}

// Member order is not meaningful, so toggling one off and back on must not read
// as a change. Same helper the Projects drawers use for their id arrays.
const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const emptyDraft = { name: '', allowJoinByLink: false, userIds: [] as string[] };

const GroupDetailsDrawer = ({ open, group, onClose }: GroupDetailsDrawerProps) => {
  const { t } = useTranslation();
  const [updateGroup, { isLoading: saving }] = useUpdateGroupMutation();
  const [deleteGroup, { isLoading: deleting }] = useDeleteGroupMutation();

  // Without `edit` the form freezes (inputs disabled, per the edit-surface
  // pattern) and Save/Discard never show; Delete needs its own verb.
  const { canEdit, canDelete } = useModuleAccess('people');

  const [draft, setDraft] = useState(emptyDraft);
  const [initial, setInitial] = useState(emptyDraft);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The members section is a read-only list until "Add users" swaps in the picker.
  const [adding, setAdding] = useState(false);

  // Rows come from the DRAFT, not group.members, so staged adds/removes show
  // immediately. The selection list supplies the email (GroupMember has none);
  // anyone missing from it still renders off the group's own name.
  const { data: users = [] } = useGetUsersSelectionQuery(undefined, { skip: !open });
  const userById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const memberRows = useMemo(
    () =>
      draft.userIds.map((id) => {
        const u = userById.get(id);
        if (u) return { id, name: u.name, email: u.email };
        return {
          id,
          name: group?.members.find((m) => m.id === id)?.fullName ?? '',
          email: undefined,
        };
      }),
    [draft.userIds, userById, group],
  );

  // Seed draft + baseline from the group; re-seeds after a save (list refetch).
  useEffect(() => {
    if (!group) return;
    const seeded = {
      name: group.name,
      allowJoinByLink: group.allowJoinByLink,
      userIds: group.members.map((m) => m.id),
    };
    setDraft(seeded);
    setInitial(seeded);
    setAdding(false);
  }, [group]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  const dirty =
    !!group &&
    (draft.name !== initial.name ||
      draft.allowJoinByLink !== initial.allowJoinByLink ||
      !sameIds(draft.userIds, initial.userIds));

  const save = async () => {
    if (!group || !dirty || !draft.name.trim()) return;
    try {
      await updateGroup({
        id: group.id,
        body: {
          groupName: draft.name.trim(),
          users: draft.userIds,
          allowJoinByLink: draft.allowJoinByLink,
        },
      }).unwrap();
      toast.success(t('group_updated'));
    } catch (err) {
      toastApiError(err, t);
    }
  };

  const remove = async () => {
    if (!group) return;
    try {
      await deleteGroup(group.id).unwrap();
      toast.success(t('group_deleted'));
      setConfirmDelete(false);
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Ungated like the invite tab's Copy: reading a link already on screen is
  // not an edit.
  const copyLink = async () => {
    try {
      await copyText(joinLink);
      toast.success(t('group_joinlink_copied'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const nameMissing = !draft.name.trim();
  const memberCount = draft.userIds.length;
  // The join URL is derived from the group id under this deployment's host, not
  // a hardcoded one, so a link copied on local or develop can't enrol someone
  // against production. It only starts admitting people once the toggle above
  // is SAVED — switching it on shows the link the save will activate.
  const joinLink = group ? `${ADMIN_PANEL_URL}/invite/join-group/${group.id}` : '';

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        icon='users-round'
        title={group?.name ?? ''}
        subtitle={t(memberCount === 1 ? 'groups_members_one' : 'groups_members_other', {
          count: memberCount,
        })}
        footer={
          <div className='group-details__footer'>
            {canDelete && (
              <Button
                variant='danger'
                iconStart='trash-2'
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                {t('group_delete')}
              </Button>
            )}
            {dirty && canEdit ? (
              <span className='group-details__actions'>
                <Button variant='secondary' onClick={() => setDraft(initial)} disabled={saving}>
                  {t('group_discard')}
                </Button>
                <Button iconStart='check' onClick={save} loading={saving} disabled={nameMissing}>
                  {t('group_save')}
                </Button>
              </span>
            ) : (
              <span className='group-details__saved'>
                <Icon name='check' size={15} />
                {t('group_saved_badge')}
              </span>
            )}
          </div>
        }
      >
        {group && (
          <div className='create-group'>
            <Field
              label={t('group_field_name')}
              required
              error={nameMissing ? t('group_name_required') : undefined}
            >
              <Input
                value={draft.name}
                placeholder={t('group_field_name_ph')}
                error={nameMissing}
                disabled={!canEdit}
                onChange={(e) => patch({ name: e.target.value })}
              />
            </Field>

            {/* ---- Allow join by link ---- */}
            <div className='create-group__join'>
              <div className='create-group__join-head'>
                <span className='create-group__join-tile'>
                  <Icon name='link' size={18} />
                </span>
                <span className='create-group__join-text'>
                  <span className='create-group__join-title'>{t('group_joinlink_title')}</span>
                  <span className='create-group__join-subtitle'>
                    {t('group_joinlink_subtitle')}
                  </span>
                </span>
                <Switch
                  checked={draft.allowJoinByLink}
                  onChange={(e) => patch({ allowJoinByLink: e.target.checked })}
                  disabled={!canEdit}
                  aria-label={t('group_joinlink_title')}
                />
              </div>

              {draft.allowJoinByLink && (
                <div className='create-group__joinlink'>
                  <div className='create-group__joinlink-main'>
                    <span className='create-group__joinlink-label'>
                      {t('group_joinlink_label')}
                    </span>
                    <div className='create-group__joinlink-row'>
                      <Input readOnly value={joinLink} />
                      <Button variant='secondary' iconStart='copy' onClick={copyLink}>
                        {t('group_joinlink_copy')}
                      </Button>
                    </div>
                    <p className='create-group__qr-hint'>
                      <Icon name='scan-line' size={16} />
                      {t('group_joinlink_qr_hint')}
                    </p>
                  </div>
                  <span className='create-group__qr'>
                    <QRCodeSVG value={joinLink} size={140} bgColor='#ffffff' fgColor='#0c1116' />
                  </span>
                </div>
              )}
            </div>

            {/* ---- Users in this group ---- */}
            <div className='group-members'>
              <div className='group-members__head'>
                <span className='group-members__title'>{t('group_users_in_group')}</span>
                <Chip variant='neutral'>{memberRows.length}</Chip>
                {canEdit &&
                  (adding ? (
                    <Button size='sm' iconStart='check' onClick={() => setAdding(false)}>
                      {t('group_done_adding')}
                    </Button>
                  ) : (
                    <Button
                      size='sm'
                      variant='secondary'
                      iconStart='user-round-plus'
                      onClick={() => setAdding(true)}
                    >
                      {t('group_add_users')}
                    </Button>
                  ))}
              </div>

              {adding ? (
                <UsersPicker
                  value={draft.userIds}
                  onChange={(ids) => patch({ userIds: ids })}
                  skip={!open}
                />
              ) : memberRows.length === 0 ? (
                <div className='group-members__empty'>
                  <p className='group-members__empty-title'>{t('group_users_empty_title')}</p>
                  <p className='group-members__empty-body'>{t('group_users_empty_body')}</p>
                </div>
              ) : (
                <div className='group-members__list'>
                  {memberRows.map((r) => (
                    <div key={r.id} className='group-members__row'>
                      <Avatar initials={initialsOf(r.name)} tone={toneOf(r.id)} size={32} />
                      <span className='group-members__identity'>
                        <span className='group-members__name'>{r.name}</span>
                        {r.email && <span className='group-members__email'>{r.email}</span>}
                      </span>
                      {canEdit && (
                        <IconButton
                          icon='user-round-minus'
                          variant='ghost'
                          aria-label={t('group_user_remove')}
                          onClick={() =>
                            patch({ userIds: draft.userIds.filter((x) => x !== r.id) })
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>

      {confirmDelete && group && (
        <ConfirmModal
          open
          onClose={() => setConfirmDelete(false)}
          onConfirm={remove}
          tone='danger'
          icon='trash-2'
          title={t('group_delete_confirm_title', { name: group.name })}
          description={t('group_delete_confirm_body')}
          confirmLabel={t('group_delete_confirm')}
          confirmVariant='danger'
          loading={deleting}
        />
      )}
    </>
  );
};

export default GroupDetailsDrawer;
